import { Temporal } from "temporal-polyfill"
import { z } from "zod"

export const YMDSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
export const ISOSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/)

const pad02 = (num: number) => num.toString().padStart(2, "0")

export class LocalDate {
	date: string

	constructor(year: number, month: number, day: number) {
		if (year < 1000 || year > 9999)
			throw new Error(`Year must be between 1000 and 9999, got ${year}`)
		if (month < 1 || month > 12)
			throw new Error(`Month must be between 1 and 12, got ${month}`)
		if (day < 1 || day > 31)
			throw new Error(`Day must be between 1 and 31, got ${day}`)

		this.date = `${year}-${pad02(month)}-${pad02(day)}`
	}

	toString(): string {
		return this.date
	}
	toIsoStringDayStartLocalTz(): string {
		return `${this.date}T00:00:00.000Z`
	}
	toIsoStringDayStartUTC(): string {
		const [year, month, day] = this.date.split("-").map(Number)
		const date = new Date(Date.UTC(year, month - 1, day))
		return date.toISOString()
	}
	// same as toIsoStringDayStartUTC, but returns the current time of day in the selected timezone
	toIsoStringUTCTimeNow(): string {
		const [year, month, day] = this.date.split("-").map(Number)
		const now = new Date()
		const [hour, minute, second] = [
			now.getHours(),
			now.getMinutes(),
			now.getSeconds(),
		]
		const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
		return date.toISOString()
	}

	static today(): LocalDate {
		const now = new Date()
		return LocalDate.fromJSDate(now)
	}
	static fromYMD(ymd: string): LocalDate {
		// console.log(`YMD: ${ymd}`)
		YMDSchema.parse(ymd)
		const [year, month, day] = ymd.split("-").map(Number)
		return new LocalDate(year, month, day)
	}
	static fromIsoStringDayStart(isoString: string): LocalDate {
		// console.log(`ISO string: ${isoString}`)
		ISOSchema.parse(isoString)
		const [date, _time] = isoString.split("T")
		return LocalDate.fromYMD(date)
	}
	static fromString(date: string): LocalDate | undefined {
		if (YMDSchema.safeParse(date).success) return LocalDate.fromYMD(date)
		if (ISOSchema.safeParse(date).success)
			return LocalDate.fromIsoStringDayStart(date)

		return undefined
	}
	static fromJSDate(date: Date): LocalDate {
		return new LocalDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
	}
}

export class PlainDate {
	date: string

	constructor(date: string) {
		const iso = ISOSchema.safeParse(date)
		if (iso.success) {
			date = date.split("T")[0]
		}
		const result = YMDSchema.safeParse(date)
		if (!result.success) {
			throw new Error(`Invalid date format. Expected YYYY-MM-DD, got ${date}`)
		}

		const [year, month, day] = date.split("-").map(Number)

		if (year < 1000 || year > 9999)
			throw new Error(`Year must be between 1000 and 9999, got ${year}`)
		if (month < 1 || month > 12)
			throw new Error(`Month must be between 1 and 12, got ${month}`)
		if (day < 1 || day > 31)
			throw new Error(`Day must be between 1 and 31, got ${day}`)

		this.date = date
	}

	static from(date: string | PlainDate): PlainDate {
		if (date instanceof PlainDate) return date
		return new PlainDate(date)
	}

	formatDate() {
		return this.toZonedDateTime().toLocaleString("en-US", {
			month: "short",
			year: "numeric",
			day: "numeric",
		})
	}
	formatWeekdayDate() {
		return this.toZonedDateTime().toLocaleString("en-US", {
			weekday: "short",
			month: "short",
			day: "numeric",
		})
	}
	formatShortMonthDay() {
		return this.toZonedDateTime().toLocaleString("en-US", {
			month: "short",
			day: "numeric",
		})
	}

	formatShortWeekday() {
		return this.toZonedDateTime().toLocaleString("en-US", { weekday: "short" })
	}

	toZonedDateTime() {
		return Temporal.PlainDate.from(this.date).toZonedDateTime(
			Temporal.Now.timeZoneId()
		)
	}
	toTemporal() {
		return Temporal.PlainDate.from(this.date)
	}

	year() {
		return this.toTemporal().year
	}

	static formatDateRange(startDate: PlainDate | string, endDate: PlainDate | string) {
		return `${PlainDate.from(startDate).formatDate()} – ${PlainDate.from(
			endDate
		).formatDate()}`
	}

	static isBetween(
		date: PlainDate | string,
		start: PlainDate | string,
		end: PlainDate | string
	): boolean {
		const checkDate = PlainDate.from(date).toTemporal()
		const startDate = PlainDate.from(start).toTemporal()
		const endDate = PlainDate.from(end).toTemporal()
		return (
			Temporal.PlainDate.compare(checkDate, startDate) >= 0 &&
			Temporal.PlainDate.compare(checkDate, endDate) <= 0
		)
	}

	static compare(one: PlainDate | string, two: PlainDate | string): number {
		const a = PlainDate.from(one).toTemporal()
		const b = PlainDate.from(two).toTemporal()
		return Temporal.PlainDate.compare(a, b)
	}
}

export class PlainDateTime {
	date: PlainDate
	timeMinutes: number

	constructor(date: PlainDate, timeMinutes: number) {
		this.date = date
		this.timeMinutes = timeMinutes
	}

	static fromMinutes(date: PlainDate | string, timeMinutes: number) {
		return new PlainDateTime(PlainDate.from(date), timeMinutes)
	}

	static fromTemporal(temporal: Temporal.PlainTime, date: PlainDate | string) {
		const timeMinutes = temporal.hour * 60 + temporal.minute
		return new PlainDateTime(PlainDate.from(date), timeMinutes)
	}

	toZonedDateTime() {
		const hours = Math.floor(this.timeMinutes / 60)
		const minutes = this.timeMinutes % 60

		const time = Temporal.PlainTime.from({
			hour: hours,
			minute: minutes,
		})
		return Temporal.PlainTime.from(time).toZonedDateTime({
			timeZone: Temporal.Now.timeZoneId(),
			plainDate: Temporal.PlainDate.from(this.date.date),
		})
	}

	formatTime() {
		return this.toZonedDateTime().toLocaleString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		})
	}
}
